import type { Campaign, CampaignFormData } from "@/types/campaign";

// Helper function to convert Campaign to CampaignFormData
export function convertCampaignToFormData(campaign: Campaign): CampaignFormData {
  // Determine discount type and value
  const discountType =
    campaign.discountType === "PERCENTAGE" ? "percentage" : "fixed";

  // For percentage, multiply by 100 to show as 25 instead of 0.25
  // For fixed amount, keep as is
  const discountValue =
    campaign.discountType === "PERCENTAGE"
      ? campaign.discountValue * 100
      : campaign.discountValue;

  // Determine minimum requirements
  let minimumRequirements: "none" | "amount" | "quantity" = "none";
  let minimumAmount: number | "" = "";
  let minimumQuantity: number | "" = "";

  if (campaign.minimumPurchaseAmount) {
    minimumRequirements = "amount";
    minimumAmount = parseFloat(campaign.minimumPurchaseAmount);
  } else if (campaign.minimumQuantity) {
    minimumRequirements = "quantity";
    minimumQuantity = campaign.minimumQuantity;
  }

  // Determine max discount uses
  let maxDiscountUses: "unlimited" | "limited" | "onePerCustomer" =
    "unlimited";
  let maxUsesLimit: number | "" = "";

  if (campaign.appliesOncePerCustomer) {
    maxDiscountUses = "onePerCustomer";
  } else if (
    campaign.usageLimit &&
    campaign.usageLimit.toLowerCase() !== "unlimited"
  ) {
    // Extract the number from strings like "5 total" or "5 total (once per customer)"
    const match = campaign.usageLimit.match(/\d+/);
    if (match) {
      maxDiscountUses = "limited";
      maxUsesLimit = parseInt(match[0], 10);
    }
  }

  // Determine applies to
  const appliesTo =
    campaign.targetAllProducts === "All products" ||
    campaign.targetAllProducts === "true" ||
    campaign.targetAllProducts === "all"
      ? "all"
      : "specific";

  // Get product IDs - convert to Shopify GID format if needed
  const selectedProductIds = campaign.entitledProductIds
    ? campaign.entitledProductIds.map((id) => {
        // If it's already a GID, keep it, otherwise convert it
        if (id.startsWith("gid://shopify/Product/")) {
          return id;
        }
        return `gid://shopify/Product/${id}`;
      })
    : [];

  return {
    campaignName: campaign.title,
    discountCode: campaign.code,
    discountType,
    discountValue,
    currency: "USD",
    minimumRequirements,
    minimumAmount,
    minimumQuantity,
    maxDiscountUses,
    maxUsesLimit,
    appliesTo,
    selectedProductIds,
    combinations: {
      productDiscounts: campaign.combinesWith?.productDiscounts || false,
      orderDiscounts: campaign.combinesWith?.orderDiscounts || false,
      shippingDiscounts: campaign.combinesWith?.shippingDiscounts || false,
    },
  };
}
